import { Inject, Injectable, signal } from '@angular/core'
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http'
import { IMemberModel, MemberModel } from '../models/member-model'
import {
  catchError,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
  tap,
  throwError,
} from 'rxjs'
import { APP_CONFIG } from '../app/app.config'
import { AppConfigModel } from '../models/app-config.model'
import { GroupModel, IGroupModel } from '../models/group-model'

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  public groupModel = signal<GroupModel[]>([])

  constructor(
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {
    this.config.backend_url
  }

  public getUser(): Observable<GroupModel[]> {
    const httpOptions = {}

    const observable$ = this.http
      .get<IGroupModel[]>(
        `${this.config.backend_url}/api/identity/users`,
        httpOptions,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Capture the error and re-throw with status and message
          return throwError(() => error)
        }),
        map((groupModels) => groupModels.map((gm) => new GroupModel(gm))),
        tap({
          next: (groupModels) => {
            this.groupModel.set(groupModels)
          },
        }),
      )
    return observable$
  }

  public inviteUserInProductAndGroup(
    mail: string,
    groupName: string,
    productName: string,
  ): Observable<string> {
    const httpOptions = {
      responseType: 'text' as 'json',
    }
    const requestBody = {
      mail: mail,
      productName: productName,
    }
    const inviteUser$ = this.http
      .put<string>(
        `${this.config.backend_url}/api/groups/${groupName}/users`,
        requestBody,
        httpOptions,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Capture the error and re-throw with status and message
          return throwError(() => error)
        }),
      )
    return inviteUser$
  }

  public updateUserInGroup(
    memberModel: IMemberModel,
    group: string,
    product: string,
  ): Observable<string> {
    const httpOptions = {
      responseType: 'text' as 'json',
    }

    const requestBody = {
      id: memberModel.userId,
      roles: memberModel.roles,
      backendScopes: memberModel.scopes,
      group: group,
      product: product,
      backendScopesExpiry: memberModel.scopesExpiry,
    }

    const updateUser$ = this.http
      .put<string>(
        `${this.config.backend_url}/api/users/${memberModel.userId}`,
        requestBody,
        httpOptions,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Capture the error and re-throw with status and message
          return throwError(() => error)
        }),
      )
    return updateUser$
  }

  public removeUserFromGroup(
    userId: string,
    groupName: string,
    productName: string,
  ): Observable<string> {
    const httpOptions = {
      responseType: 'text' as 'json',
      params: new HttpParams()
        .set('userId', userId)
        .set('groupName', groupName)
        .set('productName', productName),
      body: {},
    }
    const removeUser$ = this.http
      .delete<string>(
        `${this.config.backend_url}/api/groups/${groupName}/users/${userId}`,
        httpOptions,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Capture the error and re-throw with status and message
          return throwError(() => error)
        }),
      )
    return removeUser$
  }
}
